import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Download, Printer } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ordersAPI } from '../lib/api';
import { formatPrice, formatDateTime, ORDER_STATUS } from '../lib/utils';
import { usePagination, Pagination } from '../components/Pagination';
import { printContent } from '../lib/print';

export const ClientOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        const loadOrders = async () => {
            try {
                const res = await ordersAPI.getAll();
                setOrders(res.data.orders);
            } catch (error) {
                console.error('Error loading orders:', error);
            } finally {
                setLoading(false);
            }
        };
        loadOrders();
    }, []);

    const filteredOrders = statusFilter === 'all' 
        ? orders 
        : orders.filter(o => o.status === statusFilter);

    const { paginatedItems: paginatedOrders, currentPage, totalPages, setCurrentPage, totalItems } = usePagination(filteredOrders, 50);

    const generateOrdersListHTML = () => {
        const rows = filteredOrders.map(order => `
            <tr>
                <td>${order.order_number}</td>
                <td>${formatDateTime(order.created_at)}</td>
                <td>${order.items?.length || 0}</td>
                <td style="font-weight:600">${formatPrice(order.total)}</td>
                <td>${ORDER_STATUS[order.status]?.label || order.status}</td>
            </tr>`).join('');
        return `
            <h2 style="margin-bottom:16px">Mes Commandes (${filteredOrders.length})</h2>
            <table style="width:100%;border-collapse:collapse">
                <thead><tr style="background:#1A4D2E;color:white">
                    <th style="padding:8px;text-align:left">N Commande</th>
                    <th style="padding:8px;text-align:left">Date</th>
                    <th style="padding:8px;text-align:left">Articles</th>
                    <th style="padding:8px;text-align:left">Total</th>
                    <th style="padding:8px;text-align:left">Statut</th>
                </tr></thead>
                <tbody>${rows}</tbody>
            </table>`;
    };

    const handlePrint = () => {
        printContent('Mes Commandes', generateOrdersListHTML());
    };

    const handleDownload = () => {
        const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Mes Commandes</title>
            <style>body{font-family:sans-serif;padding:20px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#1A4D2E;color:white}</style>
            </head><body><h1>Mes Commandes</h1>${generateOrdersListHTML()}</body></html>`;
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `commandes_${new Date().toISOString().slice(0, 10)}.html`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-muted/50 flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Chargement...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-muted/50" data-testid="client-orders-page">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <h1 className="font-heading text-3xl font-bold tracking-tight">
                        Mes commandes
                    </h1>
                    <div className="flex gap-2">
                        {filteredOrders.length > 0 && (
                            <>
                                <Button variant="outline" size="sm" onClick={handleDownload} data-testid="download-orders-btn">
                                    <Download className="h-4 w-4 mr-2" />
                                    Telecharger
                                </Button>
                                <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-orders-btn">
                                    <Printer className="h-4 w-4 mr-2" />
                                    Imprimer
                                </Button>
                            </>
                        )}
                        <Button asChild className="bg-leaf hover:bg-leaf/90">
                            <Link to="/catalogue">Nouvelle commande</Link>
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                <Card className="mb-6">
                    <CardContent className="pt-6">
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="w-full sm:w-[200px]" data-testid="status-filter">
                                    <SelectValue placeholder="Filtrer par statut" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous les statuts</SelectItem>
                                    {Object.entries(ORDER_STATUS).map(([key, { label }]) => (
                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Orders Table */}
                <Card>
                    <CardHeader>
                        <CardTitle className="font-heading">
                            {filteredOrders.length} commande{filteredOrders.length !== 1 ? 's' : ''}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {filteredOrders.length > 0 ? (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>N° Commande</TableHead>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Articles</TableHead>
                                            <TableHead>Total</TableHead>
                                            <TableHead>Statut</TableHead>
                                            <TableHead></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {paginatedOrders.map((order) => (
                                            <TableRow key={order.id} data-testid={`order-${order.id}`}>
                                                <TableCell className="font-medium">{order.order_number}</TableCell>
                                                <TableCell>{formatDateTime(order.created_at)}</TableCell>
                                                <TableCell>{order.items?.length || 0} article(s)</TableCell>
                                                <TableCell className="font-semibold">{formatPrice(order.total)}</TableCell>
                                                <TableCell>
                                                    <Badge className={ORDER_STATUS[order.status]?.color}>
                                                        {ORDER_STATUS[order.status]?.label}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <Button asChild variant="ghost" size="sm" data-testid={`view-order-${order.id}`}>
                                                        <Link to={`/dashboard/commande/${order.id}`}>
                                                            <Eye className="h-4 w-4 mr-2" />
                                                            Détails
                                                        </Link>
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <p className="text-muted-foreground mb-4">Aucune commande trouvée</p>
                                <Button asChild className="bg-leaf hover:bg-leaf/90">
                                    <Link to="/catalogue">Passer une commande</Link>
                                </Button>
                            </div>
                        )}
                    </CardContent>
                    <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} setCurrentPage={setCurrentPage} label="commandes" />
                </Card>
            </div>
        </div>
    );
};